import { Card, Row, Col, Statistic, Table, Tag, Alert } from 'antd';
import { useQuery } from '@tanstack/react-query';
import {
  TeamOutlined,
  CloudServerOutlined,
  DatabaseOutlined,
  KeyOutlined,
  WarningOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { yasBicimle, type AynaDurumu } from '@raporgor/utils';
import { monitoringApi, type AynaSatiri } from '../api/monitoring';
import { StatCardSkeleton } from '../components/StatCardSkeleton';
import { TableSkeleton } from '../components/TableSkeleton';

/**
 * Admin panelinin açılış ekranı.
 *
 * 🔴 2026-08-19'da yeniden yazıldı. Eski hâli ÜÇ ÖLÜ TABLODAN besleniyordu ve canlıda
 * ölçüldü: `sync_history` toplam **0 satır**, `agents` yalnız 2 eski kayıt — oysa
 * 11 İttihat firması CDC aynası üzerinden çalışıyor ve 8'i o an tazeydi. Ekran şunu
 * diyordu: "Online Agent 1 / 2", "Sync (24 sa) 0", ve **"Başarı Oranı %100" (yeşil)**.
 * Yani ölü bir tablo, açılış ekranında mükemmel sağlık olarak görünüyordu.
 *
 * O "%100" iki fallback'in üst üste binmesiydi: arka uçta `syncsLast24h > 0 ? oran : 100`,
 * ön yüzde `summary?.syncSuccessRate || 100`. İkincisi ayrıca GERÇEK bir %0'ı da
 * %100 yeşil gösterirdi (`0` falsy). Kutu tümden kaldırıldı — düzeltilecek bir oran
 * değil, kaynağı olmayan bir sayıydı.
 */

const DURUM_ETIKET: Record<AynaDurumu, { renk: string; metin: string }> = {
  taze: { renk: 'green', metin: 'Taze' },
  gecikmis: { renk: 'orange', metin: 'Gecikmiş' },
  bayat: { renk: 'red', metin: 'Bayat' },
  hic: { renk: 'default', metin: 'Hiç senkron olmamış' },
};

export function Dashboard() {
  const { data: metrics, isLoading, error } = useQuery({
    queryKey: ['dashboard-metrics'],
    queryFn: monitoringApi.getDashboard,
    refetchInterval: 30000,
    refetchIntervalInBackground: false,
  });

  if (isLoading) {
    return (
      <div>
        <h2>Dashboard</h2>
        <StatCardSkeleton count={4} />
        <div style={{ marginTop: 16 }}>
          <StatCardSkeleton count={3} />
        </div>
        <div style={{ marginTop: 16 }}>
          <Card title="Ayna tazeliği">
            <TableSkeleton columns={4} rows={5} />
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Hata"
        description="Dashboard verileri yüklenirken bir hata oluştu."
        type="error"
        showIcon
      />
    );
  }

  const summary = metrics?.summary;
  const ayna = metrics?.aynaTazeligi ?? [];
  const bayat = summary?.cdcBayatAyna ?? 0;

  const aynaKolonlari = [
    { title: 'Firma', dataIndex: 'firma', key: 'firma' },
    {
      title: 'Durum', dataIndex: 'durum', key: 'durum',
      render: (d: AynaDurumu) => {
        const e = DURUM_ETIKET[d] ?? DURUM_ETIKET.hic;
        return <Tag color={e.renk}>{e.metin}</Tag>;
      },
    },
    {
      title: 'Yaş', dataIndex: 'yasSaniye', key: 'yasSaniye',
      render: (s: number | null) => yasBicimle(s),
    },
    {
      title: 'Son senkron', dataIndex: 'sonSenkronUtc', key: 'sonSenkronUtc',
      render: (d: string | null) => (d ? new Date(d).toLocaleString('tr-TR') : '—'),
    },
  ];

  return (
    <div>
      <h2>Dashboard</h2>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="Toplam Firma" value={summary?.totalFirms ?? 0} prefix={<TeamOutlined />} />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Aynası taze firma"
              value={summary?.cdcTazeAyna ?? 0}
              suffix={`/ ${summary?.cdcFirmalar ?? 0}`}
              prefix={<SyncOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="Veri Kaynağı" value={summary?.totalDataSources ?? 0} prefix={<DatabaseOutlined />} />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="API Key" value={summary?.totalApiKeys ?? 0} prefix={<KeyOutlined />} />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} sm={12} lg={8}>
          {/* Ekranın TEK eyleme çağıran kutusu. Sıfırdan büyükse bir müşteri bayat
              veri görüyor olabilir — eşik arka uçtaki CdcFreshness ile aynı. */}
          <Card>
            <div data-testid="pano-bayat-ayna" data-alarm={bayat > 0 ? 'true' : 'false'}>
              <Statistic
                title="Bayat / hiç senkron olmamış ayna"
                value={bayat}
                prefix={<WarningOutlined />}
                valueStyle={{ color: bayat > 0 ? '#cf1322' : '#52c41a' }}
              />
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          {/* 🔴 AYRI kutu: `agents` tablosu ölü değil ama KAPSAMI dar — bugün yalnız
              Beşyıldız. CDC firmalarıyla aynı kutuda toplanınca "1/2 çevrimiçi"
              gibi yanlış bir bütün üretiyordu. */}
          <Card>
            <div data-testid="pano-eski-agent">
              <Statistic
                title="Eski nesil agent (çevrimiçi)"
                value={summary?.eskiAgentOnline ?? 0}
                suffix={`/ ${summary?.eskiAgentToplam ?? 0}`}
                prefix={<CloudServerOutlined />}
              />
              <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
                Yalnız eski nesil RaporGör agent'ı. İttihat firmaları yukarıdaki ayna
                tazeliğinden izlenir.
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="Hata Logları (24 Saat)"
              value={summary?.errorLogsLast24h ?? 0}
              prefix={<WarningOutlined />}
              valueStyle={{ color: summary?.errorLogsLast24h ? '#cf1322' : '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* En bayat üstte — sıralama SUNUCUDA yapılıyor, ilk satır en çok ilgi
          isteyen firma olsun diye. */}
      <Card title="Ayna tazeliği" style={{ marginTop: 16 }}>
        <Table<AynaSatiri>
          dataSource={ayna}
          columns={aynaKolonlari}
          rowKey="firmId"
          pagination={false}
          size="small"
          onRow={(r) => ({ 'data-testid': `ayna-satir-${r.firmId}` } as React.HTMLAttributes<HTMLElement>)}
          locale={{ emptyText: 'CDC aynası kullanan firma yok' }}
        />
      </Card>
    </div>
  );
}
